// Един запис в „История на промените": кога · кой · N промени, а под него
// стойностите преди → след. Старото е приглушено и зачеркнато, новото — удебелено.
// Полетата на заглавката са с етикет вляво; редовете — с името на продукта
// (рецептата, замяната), основната стойност (к-во × цена, „Намерено") и
// подробностите на един ред под нея (партида, срок, нето…). Общите суми са
// последни и приглушени — контекст, не промяна. На телефон етикетът е над
// стойността. Записът „създаден" няма подробности.
import { useState, type ReactNode } from "react";
import { DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  formatRevisionWhen,
  type RevisionChangeSet,
  type RevisionDocumentKind,
  type RevisionEntry,
  type RevisionLineChange,
} from "./revisionDiffModel";

const BeforeAfter = ({ before, after }: { before: string | null; after: string | null }) => (
  <span className="min-w-0 break-words">
    <span className={before == null ? "text-faint" : "text-muted line-through decoration-faint"}>
      {before ?? "—"}
    </span>
    <span aria-hidden className="mx-1.5 text-faint">
      →
    </span>
    <span className={after == null ? "text-faint" : "font-semibold text-ink"}>{after ?? "—"}</span>
  </span>
);

const Tag = ({ tone, children }: { tone: "added" | "removed"; children: ReactNode }) => (
  <span
    className={`mr-1.5 inline-block whitespace-nowrap rounded-full px-2 py-px text-[11px] font-semibold leading-[1.5] ${
      tone === "added" ? "bg-ok-tint text-ok-deep" : "bg-danger-tint text-danger-ink"
    }`}
  >
    {children}
  </span>
);

const MainLabel = ({ label }: { label: string | null }) =>
  label ? <span className="text-muted">{label}: </span> : null;

const LineValue = ({ line }: { line: RevisionLineChange }) => {
  const { t } = useTranslation();
  if (line.kind === "added") {
    return (
      <span className="min-w-0 break-words">
        <Tag tone="added">+ {t("inventoryPage.revAdded")}</Tag>
        <MainLabel label={line.mainLabel} />
        <span className="font-semibold text-ink">{line.after ?? "—"}</span>
      </span>
    );
  }
  if (line.kind === "removed") {
    return (
      <span className="min-w-0 break-words">
        <Tag tone="removed">− {t("inventoryPage.revRemoved")}</Tag>
        <MainLabel label={line.mainLabel} />
        <span className="text-muted line-through decoration-faint">{line.before ?? "—"}</span>
      </span>
    );
  }
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      {line.mainChanged && (
        <span className="min-w-0 break-words">
          <MainLabel label={line.mainLabel} />
          <BeforeAfter before={line.before} after={line.after} />
        </span>
      )}
      {line.details.length > 0 && (
        <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
          {line.details.map((detail) => (
            <span key={detail.field} className="min-w-0">
              <span className="text-muted">{detail.label}: </span>
              <BeforeAfter before={detail.before} after={detail.after} />
            </span>
          ))}
        </span>
      )}
    </span>
  );
};

const ChangeList = ({ changes, isMobile }: { changes: RevisionChangeSet; isMobile: boolean }) => {
  const labelClass = `min-w-0 ${isMobile ? "pt-1.5" : ""}`;
  return (
    <div
      className={`grid px-3.5 pb-3 text-[12.5px] leading-[1.4] ${
        isMobile ? "grid-cols-1" : "grid-cols-[minmax(120px,max-content)_minmax(0,1fr)] gap-x-4 gap-y-1.5"
      }`}
    >
      {changes.header.map((field) => (
        <div key={`h-${field.field}`} className="contents">
          <span className={`${labelClass} text-muted`}>{field.label}</span>
          <BeforeAfter before={field.before} after={field.after} />
        </div>
      ))}
      {/* индексът е в ключа: два реда на един продукт в един склад са възможни */}
      {changes.lines.map((line, index) => (
        <div key={`l-${index}-${line.kind}-${line.key}`} className="contents">
          <span className={`${labelClass} break-words text-ink-2`}>
            <span className="font-medium">{line.name}</span>
            {line.context && <span className="text-muted"> · {line.context}</span>}
          </span>
          <LineValue line={line} />
        </div>
      ))}
      {changes.totals.length > 0 && (
        <div className="col-span-full mt-1 flex flex-wrap gap-x-4 gap-y-0.5 border-t border-dashed border-shade-3 pt-1.5 text-xs">
          {changes.totals.map((total) => (
            <span key={total.field}>
              <span className="text-muted">{total.label}: </span>
              <BeforeAfter before={total.before} after={total.after} />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

type Props = {
  entry: RevisionEntry;
  kind: RevisionDocumentKind;
  defaultOpen: boolean;
  isMobile: boolean;
};

export const RevisionEntryItem = ({ entry, kind, defaultOpen, isMobile }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const whoClass = "min-w-0 truncate text-[12.5px] leading-[1.3] text-body";
  const whenClass = "shrink-0 text-[12.5px] font-semibold leading-[1.3] tabular-nums text-ink";

  if (entry.type === "created") {
    return (
      <li className="flex items-baseline gap-2 px-3.5 py-2">
        <span className={whenClass}>{formatRevisionWhen(entry.createdAt)}</span>
        <span className={whoClass}>{entry.createdByName || t("inventoryPage.revUnknownUser")}</span>
        <span className="shrink-0 text-xs leading-[1.3] text-muted">
          · {t(`inventoryPage.revCreated_${kind}`)}
        </span>
      </li>
    );
  }

  const { changes } = entry;
  const hasChanges = changes.count > 0;
  return (
    <li className="border-b border-shade-2">
      <button
        type="button"
        aria-expanded={hasChanges ? open : undefined}
        disabled={!hasChanges}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full cursor-pointer items-baseline gap-2 border-none bg-transparent px-3.5 py-2 text-left hover:bg-shade-1 disabled:cursor-default disabled:hover:bg-transparent"
      >
        <span className={whenClass}>{formatRevisionWhen(entry.changedAt)}</span>
        <span className={whoClass}>{entry.changedByName || t("inventoryPage.revUnknownUser")}</span>
        <span className="shrink-0 text-xs leading-[1.3] text-muted">
          ·{" "}
          {hasChanges
            ? t("inventoryPage.revChanges", { count: changes.count })
            : t("inventoryPage.revNoValueChanges")}
        </span>
        <span className="flex-1" />
        {hasChanges && (
          <DownOutlined
            aria-hidden
            className={`self-center text-[10px] text-muted transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {open && hasChanges && <ChangeList changes={changes} isMobile={isMobile} />}
    </li>
  );
};
